// client/components/ontology-v1/FAIRRuleCard.tsx
// FAIR Rule card with expand/collapse and OWL/SHACL snippets
// Implementation Plan v1.2 §6

import React, { useState } from 'react';
import { Button } from '../primitives/Button';

export interface FairRule {
  pillar: "F" | "A" | "I" | "R";
  id: string;           // e.g., "F1", "A2", "I3", "R4"
  name: string;
  question: string;
  owl?: string;
  shacl?: string;
  swrl?: string;
}

interface FAIRRuleCardProps {
  rule: FairRule;
  passed?: boolean;  // Deprecated: use status instead
  status?: "pass" | "fail" | "unknown" | "not_applicable";
  notApplicableReason?: string;
  evaluatedBy?: 'shacl' | 'supplementary' | 'none';
  onViewEvidence?: (ruleId: string) => void;
}

const PILLAR_COLORS: Record<string, string> = {
  F: "#3b82f6", // blue-500
  A: "#10b981", // green-500
  I: "#f59e0b", // amber-500
  R: "#8b5cf6", // purple-500
};

// Contextual explanations for each FAIR rule
const RULE_EXPLANATIONS: Record<string, string> = {
  "F1": "We check that your dataset has a persistent identifier (like a DOI, Handle, or ARK) that will remain stable over time, even if the data moves to a different location. This ensures that citations and references to your data won't break.",
  "F2": "We verify that your dataset is described with rich metadata - detailed information about what the data contains, who created it, when it was created, and how it relates to other resources. This helps others understand your data without needing to download it first.",
  "F3": "We confirm that your metadata explicitly includes the persistent identifier of the dataset it describes. This creates a bidirectional link so that finding the metadata leads to the data, and vice versa.",
  "F4": "We check whether your dataset is registered or indexed in a searchable resource (like a data repository, registry, or catalog) so that researchers can discover it through search engines or discipline-specific discovery tools.",

  "A1": "We verify that your data can be retrieved using its identifier through a standardized protocol (like HTTP, FTP, or a repository API). This ensures that both humans and machines can access the data using well-known methods.",
  "A1.1": "We check that the retrieval protocol is open, free, and universally implementable - meaning anyone can access your data without needing proprietary software or paying licensing fees.",
  "A1.2": "We verify that the protocol supports authentication and authorization when needed, allowing you to control who can access sensitive or embargoed data while still making it discoverable.",
  "A2": "We confirm that even if the data itself is no longer available, the metadata describing it will persist. This ensures that researchers can still learn what the data was about and why it's no longer accessible.",

  "I1": "We check that your data uses a formal, accessible, shared, and broadly applicable language for knowledge representation (like RDF, OWL, or a standard data format) so that both humans and machines can interpret it.",
  "I2": "We verify that your metadata uses vocabularies that follow FAIR principles themselves - meaning the terms and ontologies you use to describe your data are also well-documented and accessible.",
  "I3": "We confirm that when your metadata references other resources (like related datasets, publications, or samples), it uses qualified references with persistent identifiers rather than just text descriptions.",

  "R1": "We check that your metadata includes rich attributes describing the data's provenance, lineage, and context - who created it, how it was collected, what instruments were used, and how it was processed.",
  "R1.1": "We verify that your metadata includes detailed provenance information - the full history of how the data was created, transformed, and analyzed, including all processing steps and software used.",
  "R1.2": "We confirm that your metadata documents where the data came from (origin), how it relates to other resources (associations), and what standards or protocols were followed during collection and processing.",
  "R1.3": "We check that your metadata follows community standards relevant to your discipline - using field-specific metadata schemas, controlled vocabularies, and data formats that are recognized in your research domain.",
  "R2": "We verify that your data has a clear and accessible license (like CC BY, CC0, or a domain-specific license) so that others know exactly what they're allowed to do with your data - whether they can reuse it, modify it, or redistribute it."
};

export function FAIRRuleCard({ rule, passed, status, notApplicableReason, evaluatedBy, onViewEvidence }: FAIRRuleCardProps) {
  const [expanded, setExpanded] = useState(false);
  const [copiedTab, setCopiedTab] = useState<string | null>(null);

  // Determine status (support both old passed prop and new status prop)
  const ruleStatus = status || (passed === true ? "pass" : passed === false ? "fail" : "unknown");

  const pillarColor = PILLAR_COLORS[rule.pillar] || "#6b7280";

  // Status colors: green for pass, red for fail, gray for unknown, indigo for N/A
  const statusColors = {
    pass: { border: "#22c55e", background: "rgba(34, 197, 94, 0.08)", badge: "#22c55e" },
    fail: { border: "#ef4444", background: "rgba(239, 68, 68, 0.08)", badge: "#ef4444" },
    unknown: { border: "#9ca3af", background: "rgba(156, 163, 175, 0.08)", badge: "#9ca3af" },
    not_applicable: { border: "#6366f1", background: "rgba(99, 102, 241, 0.08)", badge: "#6366f1" },
  };

  const statusLabels = {
    pass: "✓ PASSED",
    fail: "✗ FAILED",
    unknown: "? UNKNOWN",
    not_applicable: "— N/A",
  };

  const colors = statusColors[ruleStatus];

  const handleCopy = async (text: string, tab: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // Fallback for older browsers or insecure contexts
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    setCopiedTab(tab);
    setTimeout(() => setCopiedTab(null), 2000);
  };

  return (
    <div
      className="fair-rule-card"
      style={{
        border: `2px solid ${colors.border}`,
        borderRadius: "8px",
        padding: "16px",
        marginBottom: "12px",
        backgroundColor: colors.background,
        position: "relative"
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: expanded ? "12px" : "0"
        }}
      >
        <button
          type="button"
          className="rule-header"
          aria-expanded={expanded}
          aria-controls={`rule-panel-${rule.id}`}
          onClick={() => setExpanded(!expanded)}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
            cursor: "pointer",
            userSelect: "none",
            flex: "1",
            background: "transparent",
            border: "0",
            padding: 0,
            textAlign: "left"
          }}
        >
          <span
            className="pillar-badge"
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: "32px",
              height: "32px",
              borderRadius: "9999px",
              backgroundColor: pillarColor,
              color: "#fff",
              fontWeight: "bold",
              fontSize: "14px"
            }}
          >
            {rule.pillar}
          </span>

          <div>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <span
                id={`rule-header-${rule.id}`}
                style={{
                  fontWeight: "600",
                  fontSize: "16px",
                  color: "#e8eefc"
                }}
              >
                {rule.id}: {rule.name}
              </span>
              <span
                style={{
                  display: "inline-block",
                  padding: "3px 10px",
                  fontSize: "11px",
                  fontWeight: "700",
                  backgroundColor: colors.badge,
                  color: "#fff",
                  borderRadius: "4px",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px"
                }}
              >
                {statusLabels[ruleStatus]}
              </span>
            </div>
            <p style={{ fontSize: "14px", color: "#6b7280", marginTop: "4px", marginBottom: 0 }}>
              {rule.question}
            </p>
          </div>
        </button>

        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginLeft: "8px" }}>
          {/* Evaluation method badge */}
          {evaluatedBy && evaluatedBy !== 'none' && (
            <span style={{
              display: "inline-block",
              padding: "2px 8px",
              fontSize: "10px",
              fontWeight: "600",
              backgroundColor: evaluatedBy === 'shacl' ? 'rgba(59, 130, 246, 0.15)' : 'rgba(245, 158, 11, 0.15)',
              color: evaluatedBy === 'shacl' ? '#3b82f6' : '#f59e0b',
              borderRadius: "4px",
              textTransform: "uppercase",
              letterSpacing: "0.5px",
            }}>
              {evaluatedBy === 'shacl' ? 'SHACL' : 'Metadata'}
            </span>
          )}

          {onViewEvidence && ruleStatus !== 'not_applicable' && (
            <Button
              helpId="ontology.evidencePanel"
              onClick={() => onViewEvidence(rule.id)}
              style={{
                padding: "6px 12px",
                fontSize: "12px",
                backgroundColor: "rgba(255, 255, 255, 0.08)",
                border: "1px solid rgba(255, 255, 255, 0.2)",
                borderRadius: "6px",
                cursor: "pointer",
                color: "#e8eefc",
              }}
            >
              View Evidence
            </Button>
          )}

          <Button
            helpId="rule.details"
            onClick={() => setExpanded(!expanded)}
            style={{
              background: "rgba(255, 255, 255, 0.05)",
              border: "1px solid rgba(255, 255, 255, 0.15)",
              padding: "4px 8px",
              cursor: "pointer",
              fontSize: "18px",
              color: "#e8eefc",
              borderRadius: "4px",
            }}
            aria-label={expanded ? "Collapse rule details" : "Expand rule details"}
          >
            {expanded ? "▼" : "▶"}
          </Button>
        </div>
      </div>

      {/* Expanded Content */}
      {expanded && (
        <div
          id={`rule-panel-${rule.id}`}
          role="region"
          aria-labelledby={`rule-header-${rule.id}`}
          className="rule-content"
          style={{
            marginTop: "16px",
            paddingTop: "16px",
            borderTop: "1px solid #e5e7eb"
          }}
        >
          {/* N/A Explanation Block */}
          {ruleStatus === 'not_applicable' && notApplicableReason && (
            <div style={{
              marginBottom: "20px",
              padding: "12px 16px",
              backgroundColor: "rgba(99, 102, 241, 0.15)",
              borderLeft: "4px solid #6366f1",
              borderRadius: "4px",
            }}>
              <h4 style={{
                margin: "0 0 8px 0",
                fontSize: "13px",
                fontWeight: "600",
                color: "#c7d2fe",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}>
                Not Applicable
              </h4>
              <p style={{
                margin: 0,
                fontSize: "14px",
                lineHeight: "1.6",
                color: "#e8eefc",
              }}>
                {notApplicableReason}
              </p>
              <p style={{
                margin: "8px 0 0 0",
                fontSize: "12px",
                color: "#a5b4fc",
              }}>
                This rule is excluded from the FAIR score denominator.
              </p>
            </div>
          )}

          {/* SHACL Validation Result Block */}
          {ruleStatus === 'pass' && evaluatedBy === 'shacl' && (
            <div style={{
              marginBottom: "20px",
              padding: "12px 16px",
              backgroundColor: "rgba(34, 197, 94, 0.1)",
              borderLeft: "4px solid #22c55e",
              borderRadius: "4px",
            }}>
              <h4 style={{
                margin: "0 0 4px 0",
                fontSize: "13px",
                fontWeight: "600",
                color: "#86efac",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}>
                Validated by W3C SHACL
              </h4>
              <p style={{
                margin: 0,
                fontSize: "13px",
                color: "#e8eefc",
              }}>
                This rule was validated by running the micro-KG through rdf-validate-shacl against the SHACL shapes in shapes.ttl. No violations were found.
              </p>
            </div>
          )}

          {/* Contextual Explanation */}
          {RULE_EXPLANATIONS[rule.id] && (
            <div style={{
              marginBottom: "20px",
              padding: "12px 16px",
              backgroundColor: "rgba(59, 130, 246, 0.15)",
              borderLeft: "4px solid #3b82f6",
              borderRadius: "4px"
            }}>
              <h4 style={{
                margin: "0 0 8px 0",
                fontSize: "13px",
                fontWeight: "600",
                color: "#e8eefc",
                textTransform: "uppercase",
                letterSpacing: "0.5px"
              }}>
                What We're Checking
              </h4>
              <p style={{
                margin: 0,
                fontSize: "14px",
                lineHeight: "1.6",
                color: "#e8eefc"
              }}>
                {RULE_EXPLANATIONS[rule.id]}
              </p>
            </div>
          )}

          {/* OWL Snippet */}
          {rule.owl && (
            <div style={{ marginBottom: "16px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                <h4 style={{ margin: 0, fontSize: "14px", fontWeight: "600", color: "#374151" }}>
                  OWL Definition
                </h4>
                <button
                  onClick={() => handleCopy(rule.owl!, "owl")}
                  style={{
                    padding: "4px 8px",
                    fontSize: "12px",
                    backgroundColor: copiedTab === "owl" ? "#10b981" : "#f3f4f6",
                    color: copiedTab === "owl" ? "#fff" : "#374151",
                    border: "1px solid #d1d5db",
                    borderRadius: "4px",
                    cursor: "pointer"
                  }}
                >
                  {copiedTab === "owl" ? "Copied!" : "Copy"}
                </button>
              </div>
              <p style={{
                fontSize: "13px",
                color: "#6b7280",
                marginBottom: "8px",
                lineHeight: "1.5",
                fontStyle: "italic"
              }}>
                This OWL (Web Ontology Language) definition describes the semantic structure we're checking. It defines the classes (like Dataset, PersistentIdentifier), properties (relationships between them), and constraints that must exist for this rule to be satisfied.
              </p>
              <pre
                style={{
                  backgroundColor: "#0a0e1a",
                  color: "#e8eefc",
                  border: "1px solid #1c2a54",
                  borderRadius: "6px",
                  padding: "12px",
                  fontSize: "12px",
                  overflowX: "auto",
                  margin: 0,
                  fontFamily: "monospace",
                  lineHeight: "1.5"
                }}
              >
                {rule.owl}
              </pre>
            </div>
          )}

          {/* SHACL Snippet */}
          {rule.shacl && (
            <div style={{ marginBottom: "16px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                <h4 style={{ margin: 0, fontSize: "14px", fontWeight: "600", color: "#374151" }}>
                  SHACL Shape
                </h4>
                <button
                  onClick={() => handleCopy(rule.shacl!, "shacl")}
                  style={{
                    padding: "4px 8px",
                    fontSize: "12px",
                    backgroundColor: copiedTab === "shacl" ? "#10b981" : "#f3f4f6",
                    color: copiedTab === "shacl" ? "#fff" : "#374151",
                    border: "1px solid #d1d5db",
                    borderRadius: "4px",
                    cursor: "pointer"
                  }}
                >
                  {copiedTab === "shacl" ? "Copied!" : "Copy"}
                </button>
              </div>
              <p style={{
                fontSize: "13px",
                color: "#6b7280",
                marginBottom: "8px",
                lineHeight: "1.5",
                fontStyle: "italic"
              }}>
                This SHACL (Shapes Constraint Language) shape defines validation rules that are automatically checked against your data. It specifies required properties (minCount), expected data types, cardinality constraints, and provides error messages when validation fails.
              </p>
              <pre
                style={{
                  backgroundColor: "#0a0e1a",
                  color: "#e8eefc",
                  border: "1px solid #1c2a54",
                  borderRadius: "6px",
                  padding: "12px",
                  fontSize: "12px",
                  overflowX: "auto",
                  margin: 0,
                  fontFamily: "monospace",
                  lineHeight: "1.5"
                }}
              >
                {rule.shacl}
              </pre>
            </div>
          )}

          {/* SWRL Snippet (optional) */}
          {rule.swrl && (
            <div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                <h4 style={{ margin: 0, fontSize: "14px", fontWeight: "600", color: "#374151" }}>
                  SWRL Rule
                </h4>
                <button
                  onClick={() => handleCopy(rule.swrl!, "swrl")}
                  style={{
                    padding: "4px 8px",
                    fontSize: "12px",
                    backgroundColor: copiedTab === "swrl" ? "#10b981" : "#f3f4f6",
                    color: copiedTab === "swrl" ? "#fff" : "#374151",
                    border: "1px solid #d1d5db",
                    borderRadius: "4px",
                    cursor: "pointer"
                  }}
                >
                  {copiedTab === "swrl" ? "Copied!" : "Copy"}
                </button>
              </div>
              <pre
                style={{
                  backgroundColor: "#0a0e1a",
                  color: "#e8eefc",
                  border: "1px solid #1c2a54",
                  borderRadius: "6px",
                  padding: "12px",
                  fontSize: "12px",
                  overflowX: "auto",
                  margin: 0,
                  fontFamily: "monospace",
                  lineHeight: "1.5"
                }}
              >
                {rule.swrl}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
